import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import {
  ArrowLeft, BarChart3, Users, CheckCircle2, XCircle, Eye, TrendingUp, TrendingDown, AlertTriangle,
} from 'lucide-react';

const fmtDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const Tab = ({ active, onClick, icon: Icon, label, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium uppercase tracking-wide border-b-2 transition-colors flex items-center gap-2 ${
      active ? 'border-[#DC143C] text-[#DC143C]' : 'border-transparent text-gray-500 hover:text-gray-700'
    }`}
  >
    <Icon className="h-4 w-4" /> {label}
  </button>
);

export default function CourseAttempts() {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const [tab, setTab] = useState('list'); // 'list' | 'stats'
  const [attempts, setAttempts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // attempt detail open in modal

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.listCourseAttempts(courseId),
      api.getCourseQuestionStats(courseId),
    ])
      .then(([a, s]) => { setAttempts(a.data || []); setStats(s.data); })
      .catch(() => toast.error('Error al cargar intentos'))
      .finally(() => setLoading(false));
  }, [courseId]);

  const openDetail = async (attemptId) => {
    try {
      const r = await api.getAttemptDetail(attemptId);
      setDetail(r.data);
    } catch { toast.error('No se pudo cargar el detalle'); }
  };

  // Resumen general
  const totalAttempts = attempts.length;
  const passedCount = attempts.filter((a) => a.passed).length;
  const failedCount = totalAttempts - passedCount;
  const avgScore = totalAttempts ? Math.round(attempts.reduce((s, a) => s + (a.score || 0), 0) / totalAttempts) : 0;
  const passRate = totalAttempts ? Math.round((passedCount / totalAttempts) * 100) : 0;

  return (
    <DashboardLayout>
      <div className="max-w-7xl" data-testid="course-attempts-page">
        <Button variant="ghost" onClick={() => navigate('/admin/courses')} className="mb-4 flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" /> Volver a Cursos
        </Button>

        <div className="mb-2">
          <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-tight flex items-center gap-3">
            <BarChart3 className="h-7 w-7 text-[#DC143C]" />
            Intentos de Evaluación
          </h1>
          <p className="text-gray-600 mt-1">{stats?.course_title || 'Cargando...'}</p>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 my-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-testid="kpi-total">
            <p className="text-xs uppercase text-gray-500 mb-1">Intentos</p>
            <p className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="h-5 w-5 text-gray-400" /> {totalAttempts}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-green-200 p-4" data-testid="kpi-passed">
            <p className="text-xs uppercase text-gray-500 mb-1">Aprobados</p>
            <p className="text-2xl font-bold text-green-700 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" /> {passedCount}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-red-200 p-4" data-testid="kpi-failed">
            <p className="text-xs uppercase text-gray-500 mb-1">No aprobados</p>
            <p className="text-2xl font-bold text-[#DC143C] flex items-center gap-2">
              <XCircle className="h-5 w-5" /> {failedCount}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-blue-200 p-4" data-testid="kpi-pass-rate">
            <p className="text-xs uppercase text-gray-500 mb-1">% Aprobación · Promedio</p>
            <p className="text-2xl font-bold text-blue-700">{passRate}% · {avgScore}%</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-4 flex gap-2">
          <Tab active={tab === 'list'} onClick={() => setTab('list')} icon={Users} label="Intentos" testid="tab-list" />
          <Tab active={tab === 'stats'} onClick={() => setTab('stats')} icon={BarChart3} label="Stats por pregunta" testid="tab-stats" />
        </div>

        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500">Cargando...</div>
        ) : tab === 'list' ? (
          attempts.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500">
              No hay intentos registrados para este curso.
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Correctas</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Puntaje</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Resultado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Detalle</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {attempts.map((a) => (
                      <tr key={a.id} className="hover:bg-gray-50 transition-colors" data-testid="attempt-row">
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">{a.user_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{a.user_email || <span className="text-xs text-amber-600">Sin email</span>}</td>
                        <td className="px-4 py-3 text-sm text-center text-gray-700">{a.correct_answers}/{a.total_questions}</td>
                        <td className="px-4 py-3 text-sm text-center font-bold" style={{ color: a.passed ? '#10b981' : '#DC143C' }}>{a.score}%</td>
                        <td className="px-4 py-3 text-center">
                          {a.passed ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-50 border border-green-200 text-green-700 text-xs font-medium">
                              <CheckCircle2 className="h-3 w-3" /> Aprobado
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-50 border border-red-200 text-red-700 text-xs font-medium">
                              <XCircle className="h-3 w-3" /> No aprobado
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{fmtDate(a.created_at)}</td>
                        <td className="px-4 py-3 text-right">
                          <Button variant="ghost" size="sm" onClick={() => openDetail(a.id)} data-testid="view-attempt-button" title="Ver respuestas">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        ) : (
          // Stats por pregunta
          <div className="space-y-4">
            {(!stats || stats.questions.length === 0) ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500">
                Sin estadísticas todavía. Cuando los usuarios hagan intentos, aparecerán aquí.
              </div>
            ) : stats.questions.map((q, i) => {
              const rate = q.correct_rate;
              const lowAcc = rate !== null && rate < 50;
              return (
                <div key={q.question_id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-5" data-testid="question-stat-card">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs uppercase text-gray-500 mb-1">Pregunta {i + 1}</p>
                      <p className="font-medium text-gray-900">{q.question_text}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className={`flex items-center gap-1 justify-end font-bold text-2xl ${rate === null ? 'text-gray-400' : rate >= 70 ? 'text-green-600' : rate >= 50 ? 'text-amber-600' : 'text-[#DC143C]'}`}>
                        {rate === null ? '—' : `${rate}%`}
                        {rate !== null && (rate >= 70 ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />)}
                      </div>
                      <p className="text-xs text-gray-500">{q.correct_count}/{q.total_attempts} aciertos</p>
                    </div>
                  </div>
                  {lowAcc && (
                    <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>Menos del 50% acierta esta pregunta. Considera revisar su redacción o si la respuesta correcta está bien configurada.</span>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    {q.options.map((opt, oi) => {
                      const count = q.answer_distribution[String(oi)] || 0;
                      const pct = q.total_attempts > 0 ? Math.round((count / q.total_attempts) * 100) : 0;
                      const isCorrect = oi === q.correct_index;
                      return (
                        <div key={oi} className="flex items-center gap-3 text-sm">
                          <div className={`w-6 text-center font-semibold ${isCorrect ? 'text-green-600' : 'text-gray-400'}`}>
                            {isCorrect ? '✓' : ''}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className={`truncate pr-2 ${isCorrect ? 'font-medium text-gray-900' : 'text-gray-700'}`}>{opt}</span>
                              <span className="text-xs text-gray-500 flex-shrink-0">{count} ({pct}%)</span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className={isCorrect ? 'h-full bg-green-500' : 'h-full bg-gray-400'}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Modal detalle de intento */}
        <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="attempt-detail-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold uppercase tracking-tight">
                Detalle de intento
              </DialogTitle>
            </DialogHeader>
            {detail && (
              <div className="mt-2 space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2 p-3 bg-gray-50 rounded">
                  <div><span className="text-gray-500">Usuario:</span> <strong>{detail.user_name}</strong></div>
                  <div><span className="text-gray-500">Email:</span> {detail.user_email || '-'}</div>
                  <div><span className="text-gray-500">Fecha:</span> {fmtDate(detail.created_at)}</div>
                  <div><span className="text-gray-500">Puntaje:</span> <strong style={{ color: detail.passed ? '#10b981' : '#DC143C' }}>{detail.score}%</strong> ({detail.correct_answers}/{detail.total_questions})</div>
                </div>
                <div className="space-y-3">
                  {detail.details.map((d, i) => (
                    <div key={d.question_id} className={`p-3 rounded border ${d.is_correct ? 'border-green-200 bg-green-50/40' : 'border-red-200 bg-red-50/40'}`} data-testid="attempt-question-detail">
                      <div className="flex items-start gap-2 mb-2">
                        {d.is_correct ? <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" /> : <XCircle className="h-5 w-5 text-[#DC143C] flex-shrink-0" />}
                        <p className="font-medium text-gray-900">{i + 1}. {d.question_text}</p>
                      </div>
                      <div className="ml-7 space-y-1 text-xs">
                        <p><span className="text-gray-500">Respuesta del usuario:</span> <span className={d.is_correct ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>{d.submitted_text ?? '—'}</span></p>
                        {!d.is_correct && (
                          <p><span className="text-gray-500">Respuesta correcta:</span> <span className="text-green-700 font-medium">{d.correct_text ?? '—'}</span></p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
